import React, { useState, useMemo, Suspense } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useRouteContext } from "@tanstack/react-router";
import { type GridColumnProps } from "@progress/kendo-react-grid";
import { LuUsers } from "react-icons/lu";
import { FiLock } from "react-icons/fi";
import Card from "@/components/shared/Card";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import { useCSPPIDMatches, useLeaData } from "@/queries";
import { MatchLEAStatus, type CSPPIDMatchLEA } from "@/models/mid";
import { type CHLD } from "@/models/chld";
import { MIDsSearchForm, type MIDsSearchFormState } from "./MidSearchForm";
import {
  MIDDetailPopup,
  MIDDetailPopupSkeleton,
  MIDDetailPopupError,
} from "./MIDDetailPopup";
import { useRecordPermissions } from "@/components/discrepancies/data-discrepancies/hooks/useRecordPermissions";

const STATUS_LABELS = new Map<MatchLEAStatus, string>([
  [MatchLEAStatus.UNRESOLVED_ACTIVE_LEA, "Unresolved"],
  [MatchLEAStatus.UNRESOLVED_INACTIVE_LEA, "Unresolved (Inactive)"],
  [MatchLEAStatus.RESOLVED_MERGED, "Merged"],
  [MatchLEAStatus.RESOLVED_REJECTED, "Not a Match"],
  [MatchLEAStatus.RESOLVED_NO_LONGER_USED, "No Longer Used"],
]);

const STATUS_LABEL_TO_VALUE: Record<string, MatchLEAStatus> = {
  ...Object.fromEntries([...STATUS_LABELS].map(([k, v]) => [v, k])),
  Resolved: MatchLEAStatus.RESOLVED_MERGED,
};

const VALID_MID_TYPES = new Set(["Internal", "External"]);

const columns: GridColumnProps[] = [
  { id: "MIDType", field: "MIDType", title: "MID Type" },
  { id: "MIDStatus", field: "MIDStatus", title: "MID Status" },
  { id: "PrimaryCSPPID", field: "PrimaryCSPPID", title: "Original CSPPID" },
  {
    id: "SecondaryCSPPID",
    field: "SecondaryCSPPID",
    title: "Potential Duplicate",
  },
  { id: "CICN", field: "CICN", title: "CICN" },
  { id: "ChildName", field: "ChildName", title: "Child Name" },
];

const DEFAULT_FILTERS: MIDsSearchFormState = {
  midType: "",
  midStatus: MatchLEAStatus.UNRESOLVED_ACTIVE_LEA,
  csppid: "",
};

function readInitialFilters(): MIDsSearchFormState {
  if (typeof window === "undefined") return DEFAULT_FILTERS;

  const params = new URLSearchParams(window.location.search);
  const urlMidType = params.get("midType");
  const urlMidStatus = params.get("midStatus");

  const midType =
    urlMidType && VALID_MID_TYPES.has(urlMidType)
      ? urlMidType
      : DEFAULT_FILTERS.midType;

  const midStatus =
    urlMidStatus && urlMidStatus in STATUS_LABEL_TO_VALUE
      ? STATUS_LABEL_TO_VALUE[urlMidStatus]
      : DEFAULT_FILTERS.midStatus;

  return {
    midType,
    midStatus,
    csppid: "",
  };
}

function MIDsNoAccessEmptyState() {
  return (
    <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 px-4">
      <div
        className="d-flex align-items-center justify-content-center mb-3"
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          backgroundColor: "#F1F5F9",
          color: "#64748B",
        }}
      >
        <FiLock size={26} />
      </div>
      <h5 className="mb-2" style={{ color: "#1E3A5F", fontWeight: 600 }}>
        No access to MIDs
      </h5>
      <p className="mb-0" style={{ color: "#64748B", maxWidth: 420 }}>
        Contact your administrator to request access.
      </p>
    </div>
  );
}

class MIDDetailErrorBoundary extends React.Component<
  { onClose: () => void; children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { onClose: () => void; children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <MIDDetailPopupError
          error={this.state.error}
          onClose={this.props.onClose}
        />
      );
    }
    return this.props.children;
  }
}

export function MIDsTab() {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const params = useParams({ from: "/$leaid" });
  const leaid = params.leaid as string;

  const initialFilters = useMemo(readInitialFilters, []);

  const [filters, setFilters] = useState<MIDsSearchFormState>(initialFilters);
  const queryClient = useQueryClient();
  const [selectedMatchLEA, setSelectedMatchLEA] =
    useState<CSPPIDMatchLEA | null>(null);

  const { canViewRecord } = useRecordPermissions();

  const { data: matchesData, error: matchesError } = useCSPPIDMatches(leaid);
  const { data: leaData, error: leaError } = useLeaData(
    leaContext.currentUserOrg.esiIdPath
  );
  const error = matchesError ?? leaError;

  const tableData = useMemo(() => {
    if (!matchesData) return [];

    const leaChildren: CHLD[] = leaData?.chld ?? [];

    return matchesData
      .filter((matchLEA) => {
        const csppid1 = matchLEA.Match?.CSPPID ?? "";
        const csppid2 = matchLEA.Match?.MatchedCSPPID ?? "";
        const midType = matchLEA.IsInternal ? "Internal" : "External";

        if (filters.midStatus !== "" && matchLEA.Status !== filters.midStatus) {
          return false;
        }

        if (filters.midType !== "" && midType !== filters.midType) {
          return false;
        }

        if (
          filters.csppid &&
          !csppid1.toLowerCase().includes(filters.csppid.toLowerCase()) &&
          !csppid2.toLowerCase().includes(filters.csppid.toLowerCase())
        ) {
          return false;
        }

        return true;
      })
      .map((matchLEA) => {
        const csppid1 = matchLEA.Match?.CSPPID ?? "";
        const csppid2 = matchLEA.Match?.MatchedCSPPID ?? "";

        const leaChild =
          leaChildren.find((c) => c.CSPPID === csppid1) ??
          leaChildren.find((c) => c.CSPPID === csppid2);

        const childName = leaChild
          ? `${leaChild.ChildFirstName ?? ""} ${leaChild.ChildLastName ?? ""}`.trim()
          : "—";

        const cicn = leaChild?.LocalChildId ?? "—";

        return {
          MIDType: matchLEA.IsInternal ? "Internal" : "External",
          MIDStatus:
            STATUS_LABELS.get(matchLEA.Status) ?? String(matchLEA.Status),
          PrimaryCSPPID: csppid1 || "—",
          SecondaryCSPPID: csppid2 || "—",
          CICN: cicn,
          ChildName: childName,
          _matchLEA: matchLEA,
        };
      });
  }, [matchesData, leaData, filters]);

  const tableKey = `${filters.midType}-${filters.midStatus}-${filters.csppid}-${tableData.length}-${tableData.map((r) => `${r.PrimaryCSPPID}:${r.SecondaryCSPPID}`).join("|")}`;

  const handleRowClick = (event: { dataItem: (typeof tableData)[number] }) => {
    const matchLEA = event.dataItem._matchLEA as CSPPIDMatchLEA;
    setSelectedMatchLEA(matchLEA);
  };

  if (!canViewRecord("CHLD")) {
    return <MIDsNoAccessEmptyState />;
  }

  return (
    <>
      <div
        className="card rounded mb-3 overflow-hidden"
        style={{
          boxShadow:
            "0 1px 3px 0 rgb(15 23 42 / 0.08), 0 1px 2px -1px rgb(15 23 42 / 0.06)",
        }}
      >
        <div className="card-custom shadow-none p-0">
          <MIDsSearchForm initialState={initialFilters} onSearch={setFilters} />
        </div>
      </div>

      <Card className="mb-3 no-padding card-border">
        <div
          className="text-white p-3 d-flex align-items-center gap-2"
          style={{
            background: "var(--color-p3)",
            fontWeight: 500,
            borderRadius: "0.375rem 0.375rem 0px 0px",
          }}
        >
          <LuUsers size={18} />
          <span>Multiple Identifiers</span>
        </div>
        <KendoTableClientState
          key={tableKey}
          data={tableData}
          columns={columns}
          error={error}
          filterable={false}
          onRowClick={handleRowClick}
        />
      </Card>

      {selectedMatchLEA && (
        <MIDDetailErrorBoundary onClose={() => setSelectedMatchLEA(null)}>
          <Suspense
            fallback={
              <MIDDetailPopupSkeleton
                onClose={() => setSelectedMatchLEA(null)}
              />
            }
          >
            <MIDDetailPopup
              matchLEA={selectedMatchLEA}
              onClose={() => setSelectedMatchLEA(null)}
              onActionComplete={() => {
                queryClient.invalidateQueries({
                  queryKey: [`/json/csppid_match/${leaid}`],
                });
                setSelectedMatchLEA(null);
              }}
            />
          </Suspense>
        </MIDDetailErrorBoundary>
      )}
    </>
  );
}